import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { SharedByMe, SharedWithMe } from '../../../api/sharingTypes';

/**
 * ShareRow — Sprint 8 Phase C.
 *
 * One compact row in the aggregate share lists ("Available to claim",
 * "Shares I sent", "Currently with me"). Designed to fit 3-up under
 * a section header on the Share tab WITHOUT looking cramped.
 *
 * Two flavors via the `direction` prop:
 *   - 'inbound'  → row shows "from {sender}" subtitle (SharedWithMe)
 *   - 'outbound' → row shows "to {recipient}" subtitle (SharedByMe)
 *
 * Pure presentational. The parent screen wires `onPress` to the
 * right destination (claim screen, item detail, etc.).
 */

export interface ShareRowProps {
  /** Stable test selector + key. */
  shareId: number;
  itemName: string;
  /** Status string from the share row — drives the status pill. */
  status: SharedByMe['status'];
  /** "BORROW" or "DONATE" — drives a subtle accent on the type pill. */
  sharingType: SharedByMe['sharing_type'];
  /** ISO-8601 timestamp of the share creation (or claim, for inbound
   *  CLAIMED rows). Used to render the "2 days ago" tail. */
  createdAt: string;
  /** Optional return-due date (informational only). */
  expectedReturnDate?: string | null;
  /** Who the share is from (inbound) or to (outbound). The backend
   *  emits this as `"Name (email@x.com)"` — already-formatted. */
  counterparty?: string | null;
  /** Row direction — controls the subtitle prefix. */
  direction: 'inbound' | 'outbound';
  onPress: () => void;
  /** Optional override for `testID`. Default is
   *  `share-row-{direction}-{shareId}`. */
  testID?: string;
  /** Optional secondary action pill (e.g. "Remind" on Lent-Out CLAIMED
   *  rows, "Return" on Borrowed CLAIMED rows). Renders before the status
   *  pill when `onAction` is provided. Its own Pressable doesn't trigger
   *  the row's `onPress`. */
  onAction?: () => void;
  actionLabel?: string;
  actionIcon?: React.ComponentProps<typeof MaterialIcons>['name'];
  actionBusy?: boolean;
  actionTestID?: string;
}

// Status pill text must use the matching on*Container token, not the accent
// color — `primary` on `primaryContainer` (and `tertiary` on tertiaryContainer)
// is the same purple-on-purple / gold-on-gold low-contrast bug we fixed on the
// ItemState banners.
const STATUS_COPY: Record<SharedByMe['status'], { label: string; color: string; bg: string }> = {
  PENDING:   { label: 'Pending',   color: fpColors.onTertiaryContainer, bg: fpColors.tertiaryContainer },
  CLAIMED:   { label: 'Claimed',   color: fpColors.onPrimaryContainer,  bg: fpColors.primaryContainer },
  RETURNED:  { label: 'Returned',  color: fpColors.onSecondaryContainer, bg: fpColors.secondaryContainer },
  DECLINED:  { label: 'Declined',  color: fpColors.onErrorContainer,    bg: fpColors.errorContainer },
  CANCELLED: { label: 'Cancelled', color: fpColors.onSurfaceVariant,    bg: fpColors.surfaceContainer },
  COMPLETED: { label: 'Completed', color: fpColors.onTertiaryContainer, bg: fpColors.tertiaryContainer },
};

export const ShareRow: React.FC<ShareRowProps> = ({
  shareId,
  itemName,
  status,
  sharingType,
  createdAt,
  expectedReturnDate,
  counterparty,
  direction,
  onPress,
  testID,
  onAction,
  actionLabel,
  actionIcon,
  actionBusy = false,
  actionTestID,
}) => {
  const status_ = STATUS_COPY[status] ?? STATUS_COPY.PENDING;
  const counterpartyLine = counterparty
    ? direction === 'inbound'
      ? `from ${stripEmail(counterparty)}`
      : `to ${stripEmail(counterparty)}`
    : null;
  const dueLine =
    expectedReturnDate && status === 'CLAIMED'
      ? `due ${formatDate(expectedReturnDate)}`
      : null;

  return (
    <Pressable
      onPress={onPress}
      testID={testID ?? `share-row-${direction}-${shareId}`}
      style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}
      accessibilityRole="button"
      accessibilityLabel={`${itemName}, ${status_.label}${
        counterparty ? `, ${counterpartyLine}` : ''
      }`}
    >
      <View style={styles.iconCol}>
        <MaterialIcons
          name={direction === 'inbound' ? 'south-west' : 'north-east'}
          size={18}
          color={fpColors.primary}
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        {/* PR19 — allow up to 2 lines for the item name + subtitle so
            long names ("Dupli-color Acrylic Lacquer Spray Paint…")
            don't truncate behind the status pill. Mirrors the
            Recent-activity NotificationCard which already wraps to
            2 lines. */}
        <Text style={styles.title} numberOfLines={2}>
          {itemName}
        </Text>
        <Text style={styles.subtitle} numberOfLines={2}>
          {[counterpartyLine, dueLine, formatRelative(createdAt)]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </View>
      {onAction ? (
        <Pressable
          onPress={onAction}
          disabled={actionBusy}
          hitSlop={6}
          style={({ pressed }) => [
            styles.actionPill,
            pressed && { opacity: 0.85 },
            actionBusy && { opacity: 0.6 },
          ]}
          accessibilityRole="button"
          accessibilityLabel={`${actionLabel ?? 'Action'} — ${itemName}`}
          testID={actionTestID}
        >
          {actionBusy ? (
            <ActivityIndicator size="small" color={fpColors.onPrimary} />
          ) : (
            <>
              {actionIcon ? (
                <MaterialIcons name={actionIcon} size={13} color={fpColors.onPrimary} />
              ) : null}
              <Text style={styles.actionText}>{actionLabel}</Text>
            </>
          )}
        </Pressable>
      ) : (
        <View style={[styles.statusPill, { backgroundColor: status_.bg }]}>
          <Text style={[styles.statusText, { color: status_.color }]}>
            {sharingType === 'DONATE' ? 'Gift' : status_.label}
          </Text>
        </View>
      )}
      <MaterialIcons
        name="chevron-right"
        size={18}
        color={fpColors.outline}
        style={{ marginLeft: 4 }}
      />
    </Pressable>
  );
};

/** "Marcus Tran (marcus@example.com)" → "Marcus Tran".
 *  Falls back to the original string if no parenthesized email. */
function stripEmail(s: string): string {
  return s.replace(/\s*\([^)]*\)\s*$/, '').trim() || s;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  iconCol: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  statusText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    letterSpacing: 0.3,
  },
  actionPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: fpColors.primary,
    minWidth: 72,
    justifyContent: 'center',
  },
  actionText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
});
